import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

/*
 * useTeamStats — GET /api/surfaces/team/stats (the brief's GithubStats board rail, Teddy
 * 2026-07-27). Live-computed MVP-side from the GitHub mirror on every read (deliberately not
 * frozen into the daily brief), so this hook keeps the standard 5-min staleTime and the board
 * simply shows the fetch-time truth. Shape mirrors the MVP route (roleStats.role is 'technical'
 * only today; other roles slot in later without a contract break).
 */

/** One PR behind a stat tile (capped list, most-recent first) — the board's §8 traceability:
 * every number opens to the PRs it counts, each deep-linking to GitHub. */
export interface StatsPrRef {
  repoFullName: string;
  number: number;
  title?: string | null;
  url?: string | null;
  author?: string | null;
  updatedAt?: string | null;
  /* The PR's Terum note, so a stats row has an IN-APP destination and not only a GitHub link
   * (ruling C1 — the stats board was the one brief surface with no in-app exit at all). NULLISH by
   * contract: the MVP arm stamps it going forward and it is simply absent on rows read before that.
   * The correct rendering of an absent one is "that exit does not render", never a dead link. */
  conversationId?: string | null;
}

export interface TeamGithubStats {
  connected: boolean;
  windowDays: number;
  repoCount: number;
  prsInFlight: number;
  readyToMerge: number;
  mergedThisWeek: number;
  churn: { additions: number; deletions: number; changedFiles: number; commitCount: number };
  contributions: { total: number; commits: number; mergedPrs: number; authors: number };
  /** Capped evidence lists behind the three tiles (counts stay the full totals). Absent on
   * pre-traceability payloads — render tiles unclickable, never a false empty list. */
  inFlightPrs?: StatsPrRef[] | null;
  readyPrs?: StatsPrRef[] | null;
  mergedPrs?: StatsPrRef[] | null;
}

export interface TeamStatsResponse {
  status: 'ready' | 'no_team';
  roleStats?: { role: string; github?: TeamGithubStats | null } | null;
}

export function useTeamStats(enabled: boolean = true) {
  const query = useQuery({
    queryKey: ['surfaces', 'team-stats'],
    queryFn: () => apiFetch<TeamStatsResponse>('/api/surfaces/team/stats'),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
  return { ...query, data: query.data ?? undefined };
}
